import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { BasicProductInfo, DialogInputs } from "../hooks/useMaterials";
import { ReportRowType } from "../hooks/useReportRows";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { AkeneoModel } from "../api/models/akeneoModels.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { FullProductData } from "../api/models/fullProductData.models";
import usePackages from "../hooks/usePackages";
import usePrices from "../hooks/usePrices";
import { RadioButton } from "primereact/radiobutton";
import CreateAkeneoModelForm from "./modelForm";
import CreateAkeneoPackageForm from "./packageForm";
import CreateAkeneoProductForm from "./productForm";
import { Package } from "../api/models/package.models";
import { FullModel } from "../api/models/fullModel.models";
import { InfoDot } from "../components/infoDot";
import GoBackLink from "../components/goBackLink";

const StyledView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  & form {
    width: 100%;

    & > div:first-child {
      padding: 0 2rem;
    }
  }

  .form-container {
    min-height: calc(100vh - 406px);
    width: 100%;
    max-width: 850px;
    text-align: left;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  padding: 1rem 0rem;
  display: flex;
  align-items: center;
  padding: 0 2rem;
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

interface CreateProductViewProps {
  show: boolean;
  onHide: () => void;
  handleCreateNewRow: (
    data: DialogInputs,
    reportRowType: ReportRowType,
  ) => void;
  handleCreateNewRowFromModel: (inputs: {
    name: string;
    ean: string;
    model: AkeneoModel;
  }) => void;
  ean: string;
  ecomodClasses?: EcomodClass[];
  supClasses?: SupClass[];
  synkkaData?: FullProductData | FullModel;
}

const CreateProductView: React.FC<CreateProductViewProps> = ({
  onHide,
  ean,
  handleCreateNewRow,
  handleCreateNewRowFromModel,
  ecomodClasses,
  supClasses,
  synkkaData = undefined,
}) => {
  const { t } = useTranslation();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );

  const [selectedType, setSelectedType] = React.useState<
    "product" | "model" | "package"
  >("product");
  const [selectMaterialSelection, setSelectMaterialSelection] = React.useState<
    "materials" | "package"
  >("materials");

  // Shared name/ean/productNumber/comment/responsibleToReport fields, lifted here so
  // they aren't lost when the user switches between the product and model forms.
  const [basicInfoDraft, setBasicInfoDraft] = useState<BasicProductInfo>(
    () => ({
      name:
        synkkaData && "product" in synkkaData
          ? synkkaData.product.name
          : synkkaData && "model" in synkkaData
            ? synkkaData.model.name
            : "",
      ean: ean && /^\d*$/.test(ean) ? ean : "",
      productNumber: ean && !/^\d*$/.test(ean) ? ean : "",
      responsibleToReport: true,
      comment: "",
    }),
  );

  const { packages, isLoading: isLoadingPackages } = usePackages({
    companyId: companyId as number,
  });

  const currentYear = new Date().getFullYear();
  const defaultPriceYear =
    new Date().getMonth() === 0 ? currentYear - 1 : currentYear;
  const [priceYearOverride, setPriceYearOverride] = useState<number | null>(
    null,
  );
  const priceYearOptions = Array.from(
    { length: currentYear - 2024 + 2 },
    (_, i) => ({ label: String(2024 + i), value: 2024 + i }),
  );

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: priceYearOverride ?? defaultPriceYear,
  });

  const [selectedPackage, setSelectedPackage] = React.useState<Package | null>(
    null,
  );

  const getSynkkaName = (synkkaData: FullProductData | FullModel) => {
    if ("product" in synkkaData) {
      return synkkaData.product.name;
    } else if ("model" in synkkaData) {
      return synkkaData.model.name;
    }
    return "";
  };

  const getSynkkaPackageRows = (synkkaData?: FullProductData | FullModel) => {
    if (synkkaData && "packageRows" in synkkaData) {
      return synkkaData.packageRows;
    }
    return undefined;
  };

  return (
    <StyledView>
      <GoBackLink
        onClick={() => {
          onHide();
        }}
      >
        {`${t("go-back")}` || "← Go back"}
      </GoBackLink>

      {ean && !synkkaData && (
        <Description>{t("text-no-results-found-for-ean")}</Description>
      )}
      {ean && synkkaData && (
        <Description>
          <i
            style={{
              marginRight: "20px",
              fontSize: "20px",
            }}
            className="pi pi-sync"
          />
          {t("label-create-product-from-synkka-description")}
        </Description>
      )}
      <LoadingContainer
        status={
          isLoadingPackages
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <Row>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              padding: "1rem 2.5rem 2rem 2.5rem",
            }}
          >
            <div>
              <RadioButton
                inputId="product"
                name="type"
                value="product"
                checked={selectedType === "product"}
                onChange={() => setSelectedType("product")}
              />
              <InfoDot
                position="right"
                text={t("infotext-product")}
                marginLeft="10px"
              >
                <label htmlFor="product" style={{ marginLeft: "0.5rem" }}>
                  {t("label-product")}
                </label>
              </InfoDot>
            </div>
            <div>
              <RadioButton
                inputId="model"
                name="type"
                value="model"
                checked={selectedType === "model"}
                onChange={() => setSelectedType("model")}
              />
              <label htmlFor="model" style={{ marginLeft: "0.5rem" }}>
                {t("label-model")}
              </label>
            </div>
          </div>
        </Row>
        {selectedType === "product" && (
          <CreateAkeneoProductForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            onHide={onHide}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
            handleCreateNewReportRow={handleCreateNewRow}
            presetEAN={ean}
            presetName={synkkaData ? getSynkkaName(synkkaData) : undefined}
            synkkaPackageRows={getSynkkaPackageRows(synkkaData)}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
          />
        )}
        {selectedType === "model" && (
          <CreateAkeneoModelForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            onHide={onHide}
            presetEAN={ean}
            handleCreateNewRowFromModel={handleCreateNewRowFromModel}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
          />
        )}
        {selectedType === "package" && (
          <CreateAkeneoPackageForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            onHide={onHide}
            initialBasicInfo={basicInfoDraft}
            onBasicInfoChange={setBasicInfoDraft}
            pricesData={pricesData}
            priceYearOverride={priceYearOverride}
            setPriceYearOverride={setPriceYearOverride}
            priceYearOptions={priceYearOptions}
            priceDefaultYear={defaultPriceYear}
            isLoadingPrices={isLoadingPrices}
          />
        )}
      </LoadingContainer>
    </StyledView>
  );
};

export default CreateProductView;
